import { useState } from "react";
import type { CreateInstitutionalLoanPayload } from "@/lib/api";

interface Props {
  onClose: () => void;
  onConfirm: (payload: CreateInstitutionalLoanPayload) => Promise<void>;
}

type Step = "form" | "processing" | "success";

const TIPOS = [
  { value: "bancario", label: "Bancario", icon: "ri-bank-line", color: "text-indigo-600", bg: "bg-indigo-50" },
  { value: "fosis", label: "FOSIS", icon: "ri-government-line", color: "text-emerald-600", bg: "bg-emerald-50" },
  { value: "subdere", label: "SUBDERE", icon: "ri-building-line", color: "text-teal-600", bg: "bg-teal-50" },
  { value: "gore", label: "GORE", icon: "ri-map-pin-line", color: "text-violet-600", bg: "bg-violet-50" },
  { value: "particular", label: "Particular", icon: "ri-user-line", color: "text-amber-600", bg: "bg-amber-50" },
];

export default function NuevoPrestamoModal({ onClose, onConfirm }: Props) {
  const [step, setStep] = useState<Step>("form");
  const [tipo, setTipo] = useState("bancario");
  const [entidad, setEntidad] = useState("");
  const [proposito, setProposito] = useState("");
  const [monto, setMonto] = useState("");
  const [cuotas, setCuotas] = useState("");
  const [tasa, setTasa] = useState("");
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split("T")[0]);
  const [garantia, setGarantia] = useState("");
  const [responsable, setResponsable] = useState("Carlos Mendoza");
  const [observaciones, setObservaciones] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const cuotaMensual = monto && cuotas ? Math.round(Number(monto) / Number(cuotas)) : 0;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!entidad.trim()) e.entidad = "Ingrese la entidad prestamista";
    if (!proposito.trim()) e.proposito = "Describa el propósito del préstamo";
    if (!monto || Number(monto) <= 0) e.monto = "Ingrese el monto del préstamo";
    if (!cuotas || Number(cuotas) <= 0) e.cuotas = "Ingrese el número de cuotas";
    if (tasa === "" || Number(tasa) < 0) e.tasa = "Ingrese la tasa de interés (0 si es sin interés)";
    if (!fechaInicio) e.fechaInicio = "Seleccione la fecha de inicio";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const [requestError, setRequestError] = useState("");

  const handleSubmit = async () => {
    if (!validate()) return;
    setStep("processing");
    setRequestError("");
    try {
      const cuotasNumber = Number(cuotas);
      const montoNumber = Number(monto);
      const tasaNumber = Number(tasa);
      await onConfirm({
        lenderName: entidad.trim(),
        loanType: tipo as CreateInstitutionalLoanPayload["loanType"],
        purpose: proposito.trim(),
        totalAmount: montoNumber,
        installmentsTotal: cuotasNumber,
        monthlyInstallment: cuotaMensual,
        installmentsPaid: 0,
        paidAmount: 0,
        interestRate: Number.isFinite(tasaNumber) ? tasaNumber : 0,
        startDate: fechaInicio,
        status: "vigente",
        responsible: responsable.trim() || "Sin responsable",
        collateral: garantia.trim() || undefined,
        notes: observaciones.trim() || undefined,
      });
      setStep("success");
    } catch (error) {
      setStep("form");
      setRequestError(error instanceof Error ? error.message : "No se pudo registrar el préstamo");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-teal-50">
              <i className="ri-bank-line text-teal-600 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">Nuevo Préstamo</h2>
              <p className="text-xs text-gray-400">Registrar préstamo o crédito institucional</p>
            </div>
          </div>
          {step === "form" && (
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </div>

        {step === "processing" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 py-16">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal-50">
              <i className="ri-loader-4-line text-2xl text-teal-600 animate-spin" />
            </div>
            <p className="text-sm text-gray-500">Registrando préstamo...</p>
          </div>
        )}

        {step === "success" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 py-10 px-8">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100">
              <i className="ri-check-line text-3xl text-emerald-600" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-bold text-gray-800">Préstamo registrado</h3>
              <p className="text-sm text-gray-500 mt-1">El préstamo ha sido ingresado al sistema correctamente</p>
            </div>
            <div className="w-full bg-gray-50 rounded-xl p-5 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Entidad</span><span className="font-medium text-gray-800">{entidad}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Monto</span><span className="font-bold text-teal-700">${Number(monto).toLocaleString("es-CL")}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Cuotas</span><span className="font-medium text-gray-800">{cuotas} cuotas de ${cuotaMensual.toLocaleString("es-CL")}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Tasa</span><span className="font-medium text-gray-800">{tasa}% anual</span></div>
            </div>
            <button onClick={onClose} className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
              Cerrar
            </button>
          </div>
        )}

        {step === "form" && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Tipo */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Tipo de Préstamo</label>
                <div className="grid grid-cols-5 gap-2">
                  {TIPOS.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setTipo(t.value)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                        tipo === t.value ? `border-current ${t.bg}` : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${tipo === t.value ? t.bg : "bg-gray-100"}`}>
                        <i className={`${t.icon} text-base ${tipo === t.value ? t.color : "text-gray-400"}`} />
                      </div>
                      <span className={`text-xs font-semibold ${tipo === t.value ? t.color : "text-gray-400"}`}>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Entidad y propósito */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Entidad prestamista <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="Ej: BancoEstado, FOSIS, GORE La Araucanía..."
                    value={entidad}
                    onChange={(e) => { setEntidad(e.target.value); setErrors((p) => ({ ...p, entidad: "" })); }}
                    className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 ${errors.entidad ? "border-red-400" : "border-gray-200"}`}
                  />
                  {errors.entidad && <p className="text-xs text-red-500 mt-1">{errors.entidad}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Propósito del préstamo <span className="text-red-500">*</span></label>
                  <textarea
                    rows={2}
                    maxLength={300}
                    placeholder="Describa el destino de los fondos..."
                    value={proposito}
                    onChange={(e) => { setProposito(e.target.value); setErrors((p) => ({ ...p, proposito: "" })); }}
                    className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 resize-none ${errors.proposito ? "border-red-400" : "border-gray-200"}`}
                  />
                  {errors.proposito && <p className="text-xs text-red-500 mt-1">{errors.proposito}</p>}
                </div>
              </div>

              {/* Montos */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Monto total ($) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min={0}
                    placeholder="0"
                    value={monto}
                    onChange={(e) => { setMonto(e.target.value); setErrors((p) => ({ ...p, monto: "" })); }}
                    className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 ${errors.monto ? "border-red-400" : "border-gray-200"}`}
                  />
                  {errors.monto && <p className="text-xs text-red-500 mt-1">{errors.monto}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">N° de cuotas <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min={1}
                    placeholder="12"
                    value={cuotas}
                    onChange={(e) => { setCuotas(e.target.value); setErrors((p) => ({ ...p, cuotas: "" })); }}
                    className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 ${errors.cuotas ? "border-red-400" : "border-gray-200"}`}
                  />
                  {errors.cuotas && <p className="text-xs text-red-500 mt-1">{errors.cuotas}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tasa interés (% anual) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    placeholder="0"
                    value={tasa}
                    onChange={(e) => { setTasa(e.target.value); setErrors((p) => ({ ...p, tasa: "" })); }}
                    className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 ${errors.tasa ? "border-red-400" : "border-gray-200"}`}
                  />
                  {errors.tasa && <p className="text-xs text-red-500 mt-1">{errors.tasa}</p>}
                </div>
              </div>

              {/* Preview cuota */}
              {cuotaMensual > 0 && (
                <div className="flex items-center gap-3 bg-teal-50 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-100">
                    <i className="ri-calculator-line text-teal-600 text-sm" />
                  </div>
                  <div>
                    <span className="text-xs text-teal-600">Cuota mensual estimada</span>
                    <div className="text-base font-bold text-teal-700">${cuotaMensual.toLocaleString("es-CL")}</div>
                  </div>
                </div>
              )}

              {/* Fechas y otros */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de inicio <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 ${errors.fechaInicio ? "border-red-400" : "border-gray-200"}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Responsable</label>
                  <input
                    type="text"
                    value={responsable}
                    onChange={(e) => setResponsable(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Garantía</label>
                  <input
                    type="text"
                    placeholder="Ej: Activos de la APR, Aval directiva..."
                    value={garantia}
                    onChange={(e) => setGarantia(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Observaciones</label>
                  <textarea
                    rows={2}
                    maxLength={300}
                    placeholder="Notas adicionales..."
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              {requestError && (
                <p className="text-xs text-red-500">{requestError}</p>
              )}
              <button
                onClick={() => {
                  void handleSubmit();
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line" /></div>
                Registrar Préstamo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
